<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';
    import Pagination from '@/components/PaginationComponent.vue';

    // Store
    import { useCommonStore } from '@/stores/common/commonStore';
    import { useReserveStore } from '@/stores/reserve/reserveStore';
    import { useLanguageStore } from '@/stores/common/languageStore';

    // Vue
    import { ref, onMounted, inject } from 'vue';
    //import { reactive } from 'vue';
    import { useRouter } from 'vue-router';
    import { getCurrentInstance } from 'vue';

    // Utils
    import Utils from '@/commonProject/helpers/utils.js';

    // Model
    //import { ReserveListRequest } from '@/models/reserve/reserveModel';

    // ----------------------------
    // 定数
    // ----------------------------
    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const router = useRouter();
    const reserveStore = useReserveStore();
    const commonStore = useCommonStore();
    const languageStore = useLanguageStore();

    const app = getCurrentInstance();

    // ----------------------------
    // リアクティブ変数
    // ----------------------------
    const currentPage = ref(1);
    const totalPages = ref(0);

    //const reserveListRequest = new ReserveListRequest();

    // 初期化。
    reserveStore.clearAll();
    reserveStore.reserveList = [];

    // ----------------------------
    // 関数
    // ----------------------------
    // 戻るボタン押下時
    function onBack() {
        // ホーム画面に戻る
        router.push({ name: 'home' });
    }

    // ページネーション押下時
    function handlePageChange(newPage) {
        currentPage.value = newPage;
        // ページング切り替え後に一覧を再取得する。
        getReserveList();
    }
    const pagination = ref();
    /**
     * ページングの初期処理を実行する。
     * 取得したAPIのデータ数を引き渡す。
     */
    function handleOnloadPagination(RowResult) {
        if (!Utils.isNullEmpty(RowResult)) {
            pagination.value.onload(RowResult);
        }
    }

    function onReserveDetail(pk) {
        reserveStore.reservationPk = pk;
        router.push({
            name: 'reservedetail',
        });
    }

    // ----------------------------
    // ライフサイクル
    // ----------------------------
    onMounted(async () => {
        // Storeを初期化する。
        reserveStore.reserveList = [];

        // 1ページ目に戻す。
        currentPage.value = 1;

        // 予約一覧APIをコールする。
        await getReserveList();
    });

    async function getReserveList() {
        // 1ページには50行表示する。
        const maxPage = 50;

        // 予約一覧APIのリクエストボディを生成する。
        const reserveListRequest = {
            Lang: languageStore.getLanguageValue,
            PageCond: {
                RowPosition: maxPage * (currentPage.value - 1) + 1,
                RowAmount: maxPage,
                RowCount: true,
            },
        };

        // 予約一覧APIをコールする。
        const response = await reserveStore.getListAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, reserveListRequest);

        if (response.success) {
            // APIから取得した予約の個数の全件数を取得する。
            totalPages.value = reserveStore.pageInfo;

            // ページングを補正。
            handleOnloadPagination(totalPages.value);
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }
</script>

<template>
    <section id="main">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3 class="reserve">
                    <span>{{ $t('reserveList.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <article class="innerTable">
                        <table class="table reserve">
                            <thead>
                                <tr>
                                    <th />
                                    <th>
                                        <!--予約番号-->
                                        <span>{{ $t('reserveList.parts.tableHeader1') }}</span>
                                    </th>
                                    <th>
                                        <!--予約日時-->
                                        <span>{{ $t('reserveList.parts.tableHeader2') }}</span>
                                    </th>
                                    <th>
                                        <!--利用方法-->
                                        <span>{{ $t('reserveList.parts.tableHeader3') }}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(list, index) in reserveStore.reserveList" :key="index" @click="onReserveDetail(list.ReservationPk)">
                                    <td>
                                        <template v-if="list.ReservationStatus === '1'">
                                            <!--受付-->
                                            <span class="reserveTableStatus1">{{ commonStore.getConvertDictionary('ReservationStatus', list.ReservationStatus, languageStore.getLanguageValue) }}</span>
                                        </template>
                                        <template v-else-if="list.ReservationStatus === '2'">
                                            <!--確定-->
                                            <span class="reserveTableStatus2">{{ commonStore.getConvertDictionary('ReservationStatus', list.ReservationStatus, languageStore.getLanguageValue) }}</span>
                                        </template>
                                        <template v-else-if="list.ReservationStatus === '3'">
                                            <!--キャンセル-->
                                            <span class="reserveTableStatus3">{{ commonStore.getConvertDictionary('ReservationStatus', list.ReservationStatus, languageStore.getLanguageValue) }}</span>
                                        </template>
                                        <template v-else-if="list.ReservationStatus === '4'">
                                            <!--当日キャンセル-->
                                            <span class="reserveTableStatus4">{{ commonStore.getConvertDictionary('ReservationStatus', list.ReservationStatus, languageStore.getLanguageValue) }}</span>
                                        </template>
                                    </td>
                                    <td>
                                        <span>{{ list.ReservationNo }}</span>
                                    </td>
                                    <td>
                                        <span>{{ list.ReservationDt }}</span>
                                    </td>
                                    <td>
                                        <template v-if="list.CngMethodType === '1'">
                                            <!--対面-->
                                            <span class="reserveTableMethodType1">{{ commonStore.getConvertDictionary('CngMethodType', list.CngMethodType, languageStore.getLanguageValue) }}</span>
                                        </template>
                                        <template v-if="list.CngMethodType === '2'">
                                            <!--電話-->
                                            <span class="reserveTableMethodType2">{{ commonStore.getConvertDictionary('CngMethodType', list.CngMethodType, languageStore.getLanguageValue) }}</span>
                                        </template>
                                        <template v-if="list.CngMethodType === '3'">
                                            <!--オンライン-->
                                            <span class="reserveTableMethodType3">{{ commonStore.getConvertDictionary('CngMethodType', list.CngMethodType, languageStore.getLanguageValue) }}</span>
                                        </template>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </article>
                    <Pagination ref="pagination" :currentPage="currentPage" :totalPages="totalPages" @pageChanged="handlePageChange" />
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <button type="button" class="btnNormal" @click="onBack">{{ $t('common.parts.backPage') }}</button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>

<style></style>
